<template>
    <div
        dragndrop
        question
        :id="`question-${questionData.number}`"
        @keyup.enter="submitQuestion"
        :data-drag-is-active="dragIsActive"
    >
        <questionheader :questionData="questionData" />
        <div data-question-main>
            <div data-subquestion data-header :disabled="!editingAllowed" :data-sq-layout-direction="sqLayoutType">
                <strong data-section-header v-html="DOMPurify.sanitize(get.textResource(`rs_drag_drop_pick_your_answer`))"></strong>
                <div>
                    <draggable
                        :list="possibleOptions"
                        data-dragarea
                        @start="startDragging($event, true)"
                        @end="endDragging"
                        :style="`height: ${headerHeight}`"
                        role="option"
                        :group="questionData.id"
                        :disabled="!editingAllowed"
                    >
                        <div
                            data-answer
                            ref="draggable-option"
                            v-for="option in possibleOptions"
                            :key="option.id"
                            :style="`background: ${questionData.subQuestionColor}`"
                            :tabindex="dragIsActive || !editingAllowed ? -1 : 0"
                            @keydown.space="optionSpaceDown($event, option)"
                            @click="optionIsClicked(option)"
                            @touchstart="optionIsClicked(option)"
                            :aria-grabbed="clickedOption.index === option.index"
                            draggable="true"
                            :data-is-text="option.mediaType === undefined"
                        >
                            <p v-if="option.mediaType === undefined" v-html="DOMPurify.sanitize(option.label)"></p>
                            <media
                                v-else
                                :url="`${state.surveyInfo.domain}/v1/${option.url}`"
                                :alt="option.alt"
                                type="Image"
                            />
                        </div>
                    </draggable>
                </div>
            </div>
            
            <div :data-answers-horizontal="questionData.answersHorizontal" :data-col-amt="calcColAmt">
                <div
                    v-for="(answer, answerIndex) in questionData.subQuestions[0].answers"
                    data-subquestion
                    :key="answerIndex"
                    data-answers
                    :data-drag-hover="answerHoverIndex === answerIndex"
                    :disabled="!editingAllowed"
                    @dragover="dragover($event, answerIndex)"
                    @touchmove="dragover($event, answerIndex)"
                    :id="`question-${questionData.number}_answer-${answer.number}`"
                    :data-sq-layout-direction="sqLayoutType"
                >
                    <media
                        v-if="answer.media.length > 0"
                        :url="`${state.surveyInfo.domain}/v1/${answer.media[0].url}`"
                        :alt="answer.media[0].alt"
                        type="Image"
                        data-image-label
                        :id="`drag-area-title-${answer.id}`"
                    />
                    <strong v-else :id="`drag-area-title-${answer.id}`" v-html="DOMPurify.sanitize(answer.label)"></strong>
                    <div
                        :tabindex="dragIsActive && editingAllowed ? 0 : -1"
                        @keydown.space="sqIsClicked($event, answerIndex, answerIndex)"
                        @click="sqIsClicked($event, answerIndex)"
                        @touchstart="sqIsClicked($event, answerIndex)"
                        aria-dropeffect="move"
                        ref="subquestion"
                    >
                        <draggable
                            :list="dragLists[answerIndex]"
                            data-dragarea
                            :data-has-given-answers="dragLists[answerIndex].length > 0"
                            @start="startDragging($event, false)"
                            @end="endDragging"
                            ref="draggable"
                            role="option"
                            :aria-describedby="`drag-area-title-${answer.id}`"
                            :style="`background-image:url(${state.settings.assetPath}cross-icon.svg)`"
                            :group="questionData.id"
                            :disabled="!editingAllowed"
                        >
                            <div
                                data-answer
                                v-for="option in dragLists[answerIndex]"
                                :key="option.id"
                                :style="`background: ${questionData.answerColor}`"
                                :tabindex="editingAllowed ? 0 : -1"
                                @keydown.space.stop="optionSpaceDown($event, option)"
                                @click.stop="optionIsClicked(option)"
                                @touchstart.stop="optionIsClicked(option)"
                                :aria-grabbed="clickedOption.index === option.index"
                                draggable="true"
                                :data-is-text="option.mediaType === undefined"
                            >
                                <p v-if="option.mediaType === undefined" v-html="DOMPurify.sanitize(option.label)"></p>
                                <media
                                    v-else
                                    :url="`${state.surveyInfo.domain}/v1/${option.url}`"
                                    :alt="option.alt"
                                    type="Image"
                                />
                            </div>
                        </draggable>
                    </div>
                </div>
            </div>
        </div>
        <questionfooter :questionData="questionData" />
        <validationpopup :questionValidation="questionValidation" />
    </div>
</template>

<script>
import mixins from "@/global/mixins";
import draggable from "vuedraggable";

export default {
    name: `drag-drop-question`,
    mixins: [mixins.defaultQuestion],
    inject: ['DOMPurify'],
    components: {
        draggable,
    },
    data() {
        return {
            possibleOptions: [], // The draggable ansewrs
            dragLists: [], // The lists of items which the drag library uses to drag items
            allAnswers: [], // All the answers of all the subquestions in one array
            dragIsActive: false,
            answerHoverIndex: -1, // The subquestion on which the user is dragging. This is needed to manually trigger hover effects
            clickedOption: {}, // The answer that is selected by the user with spacebar, touch or click
            headerHeight: `auto`, // The height of the header, this is used to keep the header the same height while dragging
        };
    },
    computed: {
        answersGiven() {
            return this.questionData.subQuestions[0].answers.length - this.possibleOptions.length;
        },
        // If only images are used, we should show the answers next to each other instead of below each other
        sqLayoutType: function () {
            return this.questionData.subQuestionsHorizontal || this.allAnswersAreImages ? `horizontal` : `vertical`;
        },
        allAnswersAreImages: function(){
            return this.questionData.subQuestionsHorizontal || this.questionData.subQuestions.every((sq) => sq.media.length > 0);        
        },
        editingAllowed: function () {
            return this.get.editingAllowed(this.questionData) || this.state.global.showLoader;
        },
        calcColAmt: function(){
            let answerAmt = this.questionData.subQuestions[0].answers.length;
            return answerAmt > 3 ? 3 : answerAmt;
        },
    },
    watch: {
        // If something is dragged, update the questionData object accordingly.
        // This is based on the dragLists, which are the arrays that the drag and drop library drops its items among
        // The subquestions are dragged on answers, but in the model the answers are children of the subquestions. So this method makes sure that the correct answers are checked true and false 
        dragLists() {
            this.allAnswers.forEach((a) => (a.isChecked = false));

            this.dragLists.forEach((list, listIndex) => {
                let listLabels = list.map((item) => item.label || item.index);
        
                this.questionData.subQuestions
                    .filter((a) => a.media.length > 0 ? listLabels.includes(a.number) : listLabels.includes(a.text))
                    .forEach((a) => (a.answers[listIndex].isChecked = true));
            });
        },
        dragIsActive() {
            this.answerHoverIndex = -1;
        },
    },
    methods: {
        startDragging: function (e, recalculateHeight) {
            this.dragIsActive = true;

            // While dragging, we want to keep the header the same size so the elements on the page aren't jumping around
            if (recalculateHeight) this.headerHeight = `${e.target.clientHeight}px`;
        },
        endDragging: function () {
            this.dragIsActive = false;
            this.headerHeight = `auto`;
        },
        optionSpaceDown: function (e, option) {
            e.preventDefault();

            this.clickedOption = option;
            this.dragIsActive = !this.dragIsActive;

            // Scroll into view and focus on the first subquestion when an option is selected
            this.$refs[`subquestion`][0]?.scrollIntoView({ behavior: `smooth`, block: `start` });
            this.$refs[`subquestion`][0]?.focus({ preventScroll: true });
        },
        dragover: function (e, answerIndex) {
            this.answerHoverIndex = answerIndex;
        },
        optionIsClicked: function (answer) {
            this.clickedOption = answer;
            this.dragIsActive = true;
            
            // Scroll into view and focus on the first subquestion when an option is selected
            this.$refs[`subquestion`][0]?.scrollIntoView({ behavior: `smooth`, block: `start` });
            this.$refs[`subquestion`][0]?.focus({ preventScroll: true });
        },
        sqIsClicked: function (e, dragListIndex) {
            e.preventDefault();

            if (this.dragIsActive === false) return;

            // Mimic that the user had dragged something, by manipulating the dragListss
            this.dragLists.forEach((list) => {
                let itemIndex = list.findIndex((item) => item.index === this.clickedOption.index);

                if (itemIndex !== -1) list.splice(itemIndex, 1);
            });

            this.possibleOptions = this.possibleOptions.filter((a) => a.index !== this.clickedOption.index);
            this.dragIsActive = !this.dragIsActive;
            this.dragLists[dragListIndex].push(this.clickedOption);
            this.clickedOption = {};

            // Scroll in to view and select the first option that hasn't been dragged yet
            this.$refs[`draggable-option`][0]?.scrollIntoView({ behavior: `smooth`, block: `center` });
            this.$refs[`draggable-option`][0]?.focus({ preventScroll: true });
        },
    },
    created() {
        // All the answers of all the subquestions in one array
        this.allAnswers = this.questionData.subQuestions.map((sq) => sq.answers).flat();

        // All the sqs that can be dragged but in an easier format
        this.possibleOptions = this.questionData.subQuestions.map((a) => {
            if (a.media.length > 0) {
                let media = a.media[0];
                media.index = a.number;

                return media;
            } else {
                return {
                    label: a.text,
                    index: a.number
                };
            }
        });

        // Create the dragLists. These are used by the drag library to keep track of where something is dragged to
        for (let i = 0; i < this.questionData.subQuestions[0].answers.length; i++) {
            this.dragLists.push(new Array());
        }
        
        // Update the lists for if the answers of this question have already been given
        this.questionData.subQuestions.forEach((sq) => {
            sq.answers.forEach((answer, answerIndex) => {
                if(answer.isChecked) {
                    if(sq.media.length > 0) {
                        let mediaObject = sq.media[0];
                        mediaObject.index = sq.number;

                        this.dragLists[answerIndex].push(mediaObject);
                        this.possibleOptions = this.possibleOptions.filter(po => po.index !== sq.number);
                    } else {
                        this.dragLists[answerIndex].push({
                            index: sq.number,
                            label: sq.text
                        });
                        this.possibleOptions = this.possibleOptions.filter(po => po.label !== sq.text);
                    }
                }
            });
        });
    },
};
</script>
<style scoped>
[data-dragarea],
[data-answer] {
    background: white;
    border-radius: 5px;

    img {
        margin: 5px;
    }
}

[data-image-label] {
    margin-bottom: 15px;
}

[disabled] [data-dragarea] {
    background: #e4e4e4;
    pointer-events: none;
    background-image: none !important; /* overwrite the inline styling */
    opacity: 0.5;
}

[data-header] {
    margin-bottom: 80px;
    transition: 0.4s;

    [data-dragarea] {
        padding: 0;
        box-shadow: none;
    }
}

[data-section-header] {
    font-size: 20px;
    font-size: calc(var(--AnswerStyling-font-size) * 1px);
    font-family: var(--AnswerStyling-font-family-public-name);
    color: var(--AnswerStyling-color);
}

[data-dragarea] {
    padding: 20px;
    height: 100%;
    width: 100%;
    min-height: 60px;
    max-height: 3000px;
    background-repeat: no-repeat;
    background-position: center;
    background-color: #fff;
    background-size: 0;
    display: flex;
    transition: all 0.18s ease-out, max-height 3s, background 0s;
    box-shadow: 0 0 3px #333;
}

[data-sq-layout-direction="horizontal"] {
    [data-dragarea] {
        flex-direction: row;
        flex-wrap: wrap;
        padding: 15px;
        justify-content: center;

        [data-answer] {
            width: auto;
            flex-direction: column;
            padding: 0;
            margin: 5px;
            border: 0;
            
            &[data-is-text] {
                border: 1px solid;
                padding: 15px;
                min-width: 150px;
            }

            &:hover,
            &:focus {
                transform: scale(1.07);
            }
        }
    }
}

[data-sq-layout-direction="vertical"] {
    [data-dragarea] {
        flex-direction: column;

        [data-answer] {
            width: auto;
            padding: 10px;
            justify-content: center;
            margin-top: 0;

            &:hover,
            &:focus {
                transform: scale(1.01);
            }

            &:last-child {
                margin-bottom: 0;
            }
        }
    }
}

body [data-answers-horizontal] {
    display: flex;
    flex-wrap: wrap;

    &[data-col-amt="1"] {
        [data-subquestion] {  
            width: 100%;   
        }   
    }

    &[data-col-amt="2"] {
        [data-subquestion] {  
            width: 50%;   
        }   
    }

    &[data-col-amt="3"] {
        [data-subquestion] {  
            width: 33%;   
        }   
    }

    [data-answers] [data-dragarea] {    
        align-items: flex-start;
    }

    [data-sq-layout-direction="vertical"] [data-answer] {
        width: 100%;
    }

    [data-subquestion] {
        min-height: 200px;

        > div {
            height: 100%;
        }
    }
}

@media screen and (max-width: 700px) {
    body [data-answers-horizontal] {
        &[data-col-amt="2"] {
            [data-subquestion] {  
                width: 100%;   
            }   
        }

        &[data-col-amt="3"] {
            [data-subquestion] {  
                width: 50%;   
            }   
        }
    }
}

@media screen and (max-width: 500px) {
    body [data-answers-horizontal] {
        &[data-col-amt="3"] {
            [data-subquestion] {  
                width: 100%;   
            }   
        }
    }
}

[question]:not([data-drag-is-active]) {
    [data-answer] {
        transition: all 0.2s;
    }
}

[data-answer] {
    cursor: pointer;
    border: 2px solid #d1d1d1;
    justify-content: center;

    &:hover,
    &:focus {
        filter: brightness(90%);
    }

    p {
        margin: 0;
        display: flex;
        text-align: center;
        flex-direction: column;
        pointer-events: none;
    }
}

strong {
    margin-bottom: 15px;
    font-size: calc(var(--AnswerStyling-font-size) * 1px);
    font-family: var(--AnswerStyling-font-family-public-name);
    color: var(--AnswerStyling-color);
}

[data-subquestion] {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 15px;

    > strong {
        align-self: flex-start;
    }
    
    > div {
        width: 100%;
    }
}

[data-drag-is-active] {
    [data-subquestion]:not([data-header]) {
        min-height: 110px;

        > div:focus [data-dragarea] {
            border: 1px solid var(--primary-color);
            transform: scale(1.025);
            cursor: pointer;
            filter: brightness(90%);
            max-height: 100px;
            min-height: 100px;
            box-shadow: none;
        }

        &[data-drag-hover] [data-dragarea] {
            border: 1px solid var(--primary-color);
            transform: scale(1.025);
            cursor: pointer;
            filter: brightness(90%);
            max-height: 100px;
            min-height: 100px;
            box-shadow: none;
        }

        [data-dragarea] {
            border: 1px dashed;
            border-radius: 10px;
            justify-content: center;
            align-items: center;
            max-height: 70px;
            min-height: 70px;
            background-size: 35px;

            &:hover {
                border: 1px solid var(--primary-color);
                transform: scale(1.025);
                cursor: pointer;
                filter: brightness(90%);
                max-height: 100px;
                min-height: 100px;
                box-shadow: none;
            }
        }

        [data-answer] {
            display: none;
        }
    }
}
</style>